import type { ClassOccurrence } from '@app/shared'

// ============================================================================
// WHICH CLASS IS THIS RECORDING? — REC-1c.
//
// THE OWNER'S RULE, 2026-09-15: "every recording, manual or automatic, is labelled with the
// nearest class occurrence." Start the recorder ten or thirty minutes before NEU C61 and the file
// is NEU C61's. That is a guess, and it is an honest one only because it is bounded: outside the
// window this answers `null`, and a null course is a first-class state everywhere downstream
// (`CapturedLecture.courseCode` is nullable precisely so an unfiled recording stays visibly
// unfiled rather than being filed under whatever was closest).
//
// BERKELEY TIME. A class listed 10:00 meets at 10:10 — the university-wide ten-minute grace. The
// occurrences this reads come from the Google Calendar mirror, which carries the LISTED time, so
// the real start is `startMinutes + berkeleyOffsetMinutes`. The END is not moved by the same ten:
// the owner's classes end on the listed time (owner, 2026-10-15), so the real end is
// `endMinutes + endOffsetMinutes`, three minutes for a professor's last sentence — the same
// number the phone stops its recording at (AlarmMath.DEFAULT_END_OFFSET_MINUTES). Both are
// options rather than constants because the day the upstream feed starts carrying real times,
// the fix is passing 0, not editing the arithmetic.
//
// WHY MINUTES-INTO-THE-DAY RATHER THAN A `Date`. `ClassOccurrence` states its times that way, and
// so does the app's own clock (`useClock` → `{ date, minutesIntoDay }`, which is the Worker's
// notion of the owner's day, not the device's). Converting to instants here would introduce a
// timezone the rest of the screen does not have, and a phone whose clock is on the wrong zone
// would silently label the wrong lecture.
// ============================================================================

/** A moment, in the same terms the occurrences and the app's clock use. */
export interface ClockPoint {
  date: string
  /** Minutes since local midnight. `useClock().minutesIntoDay`. */
  minutes: number
}

export interface NearestOptions {
  /** Berkeley Time: how many minutes after its listed start a class actually begins. */
  berkeleyOffsetMinutes?: number
  /**
   * How many minutes after its listed END a class still counts — and when the phone stops a
   * scheduled recording. A recording started in those minutes is still that class's; after them
   * the room is emptying and it is not.
   */
  endOffsetMinutes?: number
  /** How long BEFORE the real start a recording may begin and still be this class's. */
  windowMinutes?: number
}

export const DEFAULT_BERKELEY_OFFSET_MINUTES = 10
/** Classes end on the listed time; three minutes covers the last sentence. Mirrors AlarmMath.kt. */
export const DEFAULT_END_OFFSET_MINUTES = 3
export const DEFAULT_WINDOW_MINUTES = 45

/**
 * The class this moment belongs to, or null.
 *
 * ELIGIBILITY, in one sentence: `at` is at most `windowMinutes` before the real start, and no
 * later than the listed end plus `endOffsetMinutes`.
 *
 * THE ORDER, when more than one qualifies — and on a Tuesday with back-to-back classes more than
 * one does:
 *   1. A class that is ACTUALLY IN PROGRESS beats one that has not started. Standing in the room
 *      outranks being early for the next room.
 *   2. Otherwise the nearest real start wins.
 *   3. An exact tie goes to the earlier class, so the answer is the same every time it is asked.
 *
 * Occurrences on any other date are ignored outright: `at.date` is the owner's day, and a class
 * "45 minutes away" across a midnight boundary is not a class he is walking into.
 */
export function nearestOccurrence(
  occurrences: readonly ClassOccurrence[],
  at: ClockPoint,
  options: NearestOptions = {},
): ClassOccurrence | null {
  const offset = options.berkeleyOffsetMinutes ?? DEFAULT_BERKELEY_OFFSET_MINUTES
  const endOffset = options.endOffsetMinutes ?? DEFAULT_END_OFFSET_MINUTES
  const window = options.windowMinutes ?? DEFAULT_WINDOW_MINUTES

  let best: ClassOccurrence | null = null
  let bestStart = 0
  let bestDistance = 0
  let bestInProgress = false

  for (const occurrence of occurrences) {
    if (occurrence.date !== at.date) continue

    const start = occurrence.startMinutes + offset
    const end = realEndMinutes(occurrence, endOffset)
    if (at.minutes < start - window) continue
    if (at.minutes > end) continue

    const inProgress = at.minutes >= start
    const distance = Math.abs(at.minutes - start)

    if (best === null) {
      best = occurrence
      bestStart = start
      bestDistance = distance
      bestInProgress = inProgress
      continue
    }
    if (better(inProgress, distance, start, bestInProgress, bestDistance, bestStart)) {
      best = occurrence
      bestStart = start
      bestDistance = distance
      bestInProgress = inProgress
    }
  }

  return best
}

function better(
  inProgress: boolean,
  distance: number,
  start: number,
  bestInProgress: boolean,
  bestDistance: number,
  bestStart: number,
): boolean {
  if (inProgress !== bestInProgress) return inProgress
  if (distance !== bestDistance) return distance < bestDistance
  return start < bestStart
}

/**
 * The real start of an occurrence, Berkeley Time applied. Exported because the screen states it.
 *
 * The screen says "starts 10:10" rather than the calendar's 10:00 on purpose: the owner is
 * deciding when to press Record, and the listed time is the one number on the schedule that is
 * reliably not when anything happens.
 */
export function realStartMinutes(
  occurrence: ClassOccurrence,
  berkeleyOffsetMinutes: number = DEFAULT_BERKELEY_OFFSET_MINUTES,
): number {
  return occurrence.startMinutes + berkeleyOffsetMinutes
}

/**
 * The real end of an occurrence: its listed end plus the end offset — when the phone stops a
 * scheduled recording, and the last minute a recording started is still filed under it.
 */
export function realEndMinutes(
  occurrence: ClassOccurrence,
  endOffsetMinutes: number = DEFAULT_END_OFFSET_MINUTES,
): number {
  return occurrence.endMinutes + endOffsetMinutes
}
